// 배지 카테고리
export type BadgeCategory = 'problem' | 'attendance' | 'subject' | 'plan' | 'completion';

// 배지 ID 타입
export type BadgeId =
  // 문제 풀이 배지
  | 'problem_1'
  | 'problem_50'
  | 'problem_100'
  | 'problem_200'
  | 'problem_300'
  | 'problem_400'
  | 'problem_500'
  | 'problem_all'
  // 연속 출석 배지 (JCG-2262)
  | 'attendance_streak_3'
  | 'attendance_streak_7'
  | 'attendance_streak_30'
  // 과목별 마스터 배지 (이론)
  | 'subject_db'
  | 'subject_network-os'
  | 'subject_security-newtech'
  | 'subject_sw-design'
  | 'subject_sw-dev'
  // 과목별 마스터 배지 (코딩, JCG-2262)
  | 'subject_c-language'
  | 'subject_java'
  | 'subject_python'
  | 'subject_sql'
  | 'subject_common'
  // 플랜 배지
  | 'plan_starter'
  | 'plan_premium'
  | 'plan_complete'
  | 'plan_intelligence'
  // 기출 회차 완주 배지 (JCG-2262)
  | 'completion_first'
  | 'completion_5'
  | 'completion_all'
  | 'completion_repeat3';

// 배지 정의 인터페이스
export interface BadgeDefinition {
  id: BadgeId;
  name: string;
  description: string;
  category: BadgeCategory;
  icon: string; // 이모지 또는 아이콘 이름
  image?: string; // 감자 캐릭터 메달 이미지 경로 (없으면 icon 이모지로 폴백)
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
  requirement?: number; // 문제 풀이 배지의 경우 필요 문제 수
  subject?: string; // 과목별 배지의 경우 과목 ID
}

// 사용자가 획득한 배지
export interface UserBadge {
  badgeId: BadgeId;
  earnedAt: number; // Unix timestamp
}

// Firebase에 저장되는 사용자 배지 데이터
export interface UserBadgeData {
  userId: string;
  badges: UserBadge[];
  // 출석 기록 (페이지 경로 배열)
  visitedPages: string[];
  // 마지막 업데이트 시간
  lastUpdatedAt: number;
}

// 문제 풀이 배지 기준 (800문제 돌파로 500 단계 추가, JCG-2262)
export const PROBLEM_BADGE_THRESHOLDS = [1, 50, 100, 200, 300, 400, 500] as const;

// 배지 정의 상수
export const BADGES: Record<BadgeId, BadgeDefinition> = {
  // 문제 풀이 배지
  problem_1: {
    id: 'problem_1',
    name: '첫 걸음',
    description: '첫 번째 문제를 풀었습니다',
    category: 'problem',
    icon: '🌱',
    image: '/badges/problem_1.webp',
    rarity: 'common',
    requirement: 1,
  },
  problem_50: {
    id: 'problem_50',
    name: '도전자',
    description: '50개의 문제를 풀었습니다',
    category: 'problem',
    icon: '🔥',
    image: '/badges/problem_50.webp',
    rarity: 'uncommon',
    requirement: 50,
  },
  problem_100: {
    id: 'problem_100',
    name: '학습자',
    description: '100개의 문제를 풀었습니다',
    category: 'problem',
    icon: '📚',
    image: '/badges/problem_100.webp',
    rarity: 'rare',
    requirement: 100,
  },
  problem_200: {
    id: 'problem_200',
    name: '열공러',
    description: '200개의 문제를 풀었습니다',
    category: 'problem',
    icon: '⭐',
    image: '/badges/problem_200.webp',
    rarity: 'rare',
    requirement: 200,
  },
  problem_300: {
    id: 'problem_300',
    name: '문제 헌터',
    description: '300개의 문제를 풀었습니다',
    category: 'problem',
    icon: '💫',
    image: '/badges/problem_300.webp',
    rarity: 'epic',
    requirement: 300,
  },
  problem_400: {
    id: 'problem_400',
    name: '문제 마스터',
    description: '400개의 문제를 풀었습니다',
    category: 'problem',
    icon: '🏆',
    image: '/badges/problem_400.webp',
    rarity: 'epic',
    requirement: 400,
  },
  problem_500: {
    id: 'problem_500',
    name: '문제 정복자',
    description: '500개의 문제를 풀었습니다',
    category: 'problem',
    icon: '🎯',
    image: '/badges/problem_500.webp',
    rarity: 'epic',
    requirement: 500,
  },
  problem_all: {
    id: 'problem_all',
    name: '전설의 감자',
    description: '모든 문제를 풀었습니다',
    category: 'problem',
    icon: '👑',
    image: '/badges/problem_all.webp',
    rarity: 'legendary',
  },

  // 연속 출석 배지 (JCG-2262) — getAttendanceStreak의 연속 출석일(streak) 기준
  attendance_streak_3: {
    id: 'attendance_streak_3',
    name: '작심삼일 극복',
    description: '3일 연속 출석했습니다',
    category: 'attendance',
    icon: '📅',
    image: '/badges/attendance_streak_3.webp',
    rarity: 'uncommon',
  },
  attendance_streak_7: {
    id: 'attendance_streak_7',
    name: '일주일 개근',
    description: '7일 연속 출석했습니다',
    category: 'attendance',
    icon: '🔥',
    image: '/badges/attendance_streak_7.webp',
    rarity: 'rare',
  },
  attendance_streak_30: {
    id: 'attendance_streak_30',
    name: '한 달 개근',
    description: '30일 연속 출석했습니다',
    category: 'attendance',
    icon: '🏅',
    image: '/badges/attendance_streak_30.webp',
    rarity: 'epic',
  },

  // 과목별 마스터 배지 (이론)
  subject_db: {
    id: 'subject_db',
    name: 'DB 마스터',
    description: '데이터베이스 모든 페이지를 학습했습니다',
    category: 'subject',
    icon: '🗄️',
    image: '/main/db_gamza.webp',
    rarity: 'rare',
    subject: 'db',
  },
  'subject_network-os': {
    id: 'subject_network-os',
    name: '네트워크/OS 마스터',
    description: '네트워크/OS 모든 페이지를 학습했습니다',
    category: 'subject',
    icon: '🌐',
    image: '/main/network_gamza.webp',
    rarity: 'rare',
    subject: 'network-os',
  },
  'subject_security-newtech': {
    id: 'subject_security-newtech',
    name: '보안/신기술 마스터',
    description: '보안/신기술 모든 페이지를 학습했습니다',
    category: 'subject',
    icon: '🔐',
    image: '/main/security_gamza.webp',
    rarity: 'rare',
    subject: 'security-newtech',
  },
  'subject_sw-design': {
    id: 'subject_sw-design',
    name: 'SW 설계 마스터',
    description: 'SW 설계 모든 페이지를 학습했습니다',
    category: 'subject',
    icon: '📐',
    image: '/main/design_gamza.webp',
    rarity: 'rare',
    subject: 'sw-design',
  },
  'subject_sw-dev': {
    id: 'subject_sw-dev',
    name: 'SW 개발 마스터',
    description: 'SW 개발 모든 페이지를 학습했습니다',
    category: 'subject',
    icon: '💻',
    image: '/main/dev_gamza.webp',
    rarity: 'rare',
    subject: 'sw-dev',
  },

  // 과목별 마스터 배지 (코딩, JCG-2262)
  'subject_c-language': {
    id: 'subject_c-language',
    name: 'C언어 마스터',
    description: 'C언어 모든 페이지를 학습했습니다',
    category: 'subject',
    icon: '⚙️',
    image: '/badges/subject_c-language.webp',
    rarity: 'rare',
    subject: 'c-language',
  },
  subject_java: {
    id: 'subject_java',
    name: 'Java 마스터',
    description: 'Java 모든 페이지를 학습했습니다',
    category: 'subject',
    icon: '☕',
    image: '/badges/subject_java.webp',
    rarity: 'rare',
    subject: 'java',
  },
  subject_python: {
    id: 'subject_python',
    name: 'Python 마스터',
    description: 'Python 모든 페이지를 학습했습니다',
    category: 'subject',
    icon: '🐍',
    image: '/badges/subject_python.webp',
    rarity: 'rare',
    subject: 'python',
  },
  subject_sql: {
    id: 'subject_sql',
    name: 'SQL 마스터',
    description: 'SQL 모든 페이지를 학습했습니다',
    category: 'subject',
    icon: '🔎',
    image: '/badges/subject_sql.webp',
    rarity: 'rare',
    subject: 'sql',
  },
  subject_common: {
    id: 'subject_common',
    name: '공통 기초 마스터',
    description: '공통 기초 모든 페이지를 학습했습니다',
    category: 'subject',
    icon: '🧩',
    image: '/badges/subject_common.webp',
    rarity: 'rare',
    subject: 'common',
  },

  // 플랜 배지
  plan_starter: {
    id: 'plan_starter',
    name: 'Starter',
    description: 'Starter 이용권을 구매했습니다',
    category: 'plan',
    icon: '🎫',
    image: '/badges/plan_starter.webp',
    rarity: 'common',
  },
  plan_premium: {
    id: 'plan_premium',
    name: 'Premium',
    description: 'Premium 이용권을 구매했습니다',
    category: 'plan',
    icon: '💎',
    image: '/badges/plan_premium.webp',
    rarity: 'uncommon',
  },
  plan_complete: {
    id: 'plan_complete',
    name: 'Complete',
    description: 'Complete 이용권을 구매했습니다',
    category: 'plan',
    icon: '🎖️',
    image: '/badges/plan_complete.webp',
    rarity: 'rare',
  },
  plan_intelligence: {
    id: 'plan_intelligence',
    name: 'Intelligence',
    description: 'Intelligence 이용권을 구매했습니다',
    category: 'plan',
    icon: '🧠',
    image: '/badges/plan_intelligence.webp',
    rarity: 'legendary',
  },

  // 기출 회차 완주 배지 (JCG-2262)
  completion_first: {
    id: 'completion_first',
    name: '첫 완주',
    description: '기출 회차를 처음으로 완주했습니다',
    category: 'completion',
    icon: '🏁',
    image: '/badges/completion_first.webp',
    rarity: 'rare',
  },
  completion_5: {
    id: 'completion_5',
    name: '완주 5회차',
    description: '기출 회차 5개를 완주했습니다',
    category: 'completion',
    icon: '🔥',
    image: '/badges/completion_5.webp',
    rarity: 'epic',
  },
  completion_all: {
    id: 'completion_all',
    name: '완전 정복',
    description: '모든 기출 회차를 완주했습니다',
    category: 'completion',
    icon: '👑',
    image: '/badges/completion_all.webp',
    rarity: 'legendary',
  },
  completion_repeat3: {
    id: 'completion_repeat3',
    name: '반복의 달인',
    description: '한 기출 회차를 3회 완주했습니다',
    category: 'completion',
    icon: '🏆',
    image: '/badges/completion_repeat3.webp',
    rarity: 'epic',
  },
};

// 카테고리별 배지 그룹
export const BADGE_CATEGORIES: Record<BadgeCategory, { name: string; description: string }> = {
  problem: {
    name: '문제 풀이',
    description: '문제를 풀어서 획득하는 배지',
  },
  attendance: {
    name: '출석',
    description: '연속 출석으로 획득하는 배지',
  },
  subject: {
    name: '과목 마스터',
    description: '과목별 모든 페이지를 학습해서 획득하는 배지',
  },
  plan: {
    name: '이용권',
    description: '이용권을 구매해서 획득하는 배지',
  },
  completion: {
    name: '기출 완주',
    description: '기출 회차를 완주해서 획득하는 배지',
  },
};

// 희귀도 스타일
export const RARITY_STYLES: Record<BadgeDefinition['rarity'], { bg: string; border: string; text: string }> = {
  common: {
    bg: 'bg-gray-100 dark:bg-gray-800',
    border: 'border-gray-300 dark:border-gray-600',
    text: 'text-gray-600 dark:text-gray-400',
  },
  uncommon: {
    bg: 'bg-green-50 dark:bg-green-900/20',
    border: 'border-green-400 dark:border-green-600',
    text: 'text-green-600 dark:text-green-400',
  },
  rare: {
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    border: 'border-blue-400 dark:border-blue-600',
    text: 'text-blue-600 dark:text-blue-400',
  },
  epic: {
    bg: 'bg-purple-50 dark:bg-purple-900/20',
    border: 'border-purple-400 dark:border-purple-600',
    text: 'text-purple-600 dark:text-purple-400',
  },
  legendary: {
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    border: 'border-amber-400 dark:border-amber-600',
    text: 'text-amber-600 dark:text-amber-400',
  },
};

// 희귀도 이름
export const RARITY_NAMES: Record<BadgeDefinition['rarity'], string> = {
  common: '일반',
  uncommon: '고급',
  rare: '희귀',
  epic: '영웅',
  legendary: '전설',
};
